/* eslint-disable jsx-a11y/no-static-element-interactions */
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames';
import animateScrollTo from 'animated-scroll-to';

import { ScrollWrapper } from '../../higher_order_components/scroll_wrapper';
import { Icon } from '../icon/icon';

import styles from './scroll_to.css';

/**
 * Scroll to component
 * @desc ScrollTo will allow users to scroll to top and bottom of results pages.
 * @returns {ScrollTo} A button that will either scroll to top or bottom of pages.
 */
class ScrollTo extends Component {

    constructor(props, context) {
        super(props, context);
        const { contentProps } = this.props;
        this.handleScroll = this.handleScroll.bind(this);
        this.state = {
            displayMode: 'goToBottom',
            label: contentProps.goToBottomText
        };
    }

    /**
     * This event is run on scroll of the browser window.
     * When currentHeight is less than 0, it means the position that we want to dock it at would be within the
     * current viewport, don't need to scroll to 'see more quotes'.
     * @todo:  Review where handleScroll and how is used, this method is updating the internal the state on each call.
     */
    handleScroll = () => {
        const { contentProps, dimensions } = this.props;
        /** Calculate each time */
        const scrollTop = window.pageYOffset;

        // Get the dimensions passed from the container.
        const { currentHeight } = dimensions;

        if (currentHeight <= scrollTop) {
            // We've reached the snap point
            this.setState({ displayMode: 'goToTop', label: contentProps.goToTopText });
        } else {
            // We're scrolling down
            this.setState({ displayMode: 'goToBottom', label: contentProps.goToBottomText });
        }
    };

    /**
     * If the button is clicked on it needs to scrollTo coordinates
     * and then re-run handleScroll to update the labels etc.
     */
    handleClick = () => {
        const { displayMode } = this.state;
        const { dimensions } = this.props;
        if (displayMode === 'goToBottom') {
            animateScrollTo(dimensions.documentHeight);
        } else {
            animateScrollTo(0);
        }
        this.handleScroll();
    };

    /**
     * Return an Icon component dependent on which way we are scrolling.
     * @param {String} position A variable just for the ID to avoid using cuid.
     * @returns {Node} An icon component
     */
    getIcon = (position = 'before') => {
        const { displayMode } = this.state;
        const { contentProps } = this.props;
        if (!contentProps.arrowsEnabled) {
            return null;
        }

        const variant = displayMode === 'goToTop' ? 'ctm_angle_up' : 'ctm_angle_down';

        return (
            <Icon key={`ScrollTo.${variant}.${position}`} color="inherit" variant={variant} />
        );
    };

    /**
     * The render method
     * @returns {Node} An empty div or the scroll to button, wrapped in the scroll wrapper.
     */
    render = () => {
        const { dimensions, mediaType } = this.props;
        const { displayMode, label } = this.state;
        if (dimensions.currentHeight < 0 && window.pageYOffset === 0) {
            return null;
        }

        const classes = classNames(styles[displayMode]);
        const isXs = mediaType === 'xs';
        const content = [this.getIcon()];

        if (!isXs) {
            content.push(label);
            content.push(this.getIcon('after'));
        }

        return (
            <ScrollWrapper handleScroll={this.handleScroll}>
                <div className={classes}>
                    <a
                        data-id="seeMoreQuotes"
                        onClick={this.handleClick}
                        className={styles.link}>
                        {content}
                    </a>
                </div>
            </ScrollWrapper>
        );
    };

}

ScrollTo.propTypes = {
    // eslint-disable-next-line react/no-unused-prop-types
    visualProps: PropTypes.shape({
        variant: PropTypes.string
    }),
    contentProps: PropTypes.shape({
        anchorPosition: PropTypes.string,
        goToBottomText: PropTypes.string,
        goToTopText: PropTypes.string,
        scrollTo: PropTypes.string,
        stationaryDockingOffset: PropTypes.number,
        arrowsEnabled: PropTypes.bool
    }),
    dimensions: PropTypes.shape({
        currentHeight: PropTypes.number,
        documentHeight: PropTypes.number
    }),
    mediaType: PropTypes.string
};

ScrollTo.defaultProps = {
    contentProps: {
        goToBottomText: ' Go to Bottom ',
        goToTopText: ' Go to Top ',
        arrowsEnabled: true
    }
};

export { ScrollTo };
